import * as THREE from 'three';
import { FALL_LIP_RUN_M, type PlanetSampler, type TerrainSample } from '@hyperlight/shared';
import type { Planet } from '../planet';
import { WaterReflection } from './waterReflection';
import { RIVER_SURFACE_LIFT_M, RIVER_REFLECTION_NEAR_M, RIVER_REFLECTION_FAR_M } from './riverRibbon';
import { nearestInlandWetlandReflection } from './inlandWetlandWater';

/** One local mirror for the nearest river OR closed inland basin. Shares the
 * ocean's frame budget; wetlands do not add a second reflection pass. */
export class RiverReflection {
  private readonly mirror = new WaterReflection(512, 256);
  private material: THREE.ShaderMaterial | null = null;
  private readonly inverse = new THREE.Matrix4();
  private readonly mirrorPose = new THREE.Matrix4();
  private readonly carry = new THREE.Matrix4();
  private readonly eye = new THREE.Vector3();
  private readonly mirrorEye = new THREE.Vector3();
  private readonly candidate = new THREE.Vector3();
  private readonly anchor = new THREE.Vector3();
  private readonly normal = new THREE.Vector3();
  private readonly flow = new THREE.Vector3();
  private readonly across = new THREE.Vector3();
  private readonly probe = new THREE.Vector3();
  private readonly sample = {} as TerrainSample;
  private readonly riverCandidates=Array.from({length:8},()=>({point:new THREE.Vector3(),distance:Infinity}));
  private readonly screen = new THREE.Vector3();
  private readonly lastAnchor = new THREE.Vector3();
  private readonly lastNormal = new THREE.Vector3();
  private readonly viewPlane = new THREE.Plane();
  private readonly viewport = new THREE.Vector2();
  private readonly wetlandCandidate={point:new THREE.Vector3(),distanceSquared:Infinity,featureId:''};
  private fresh = false;
  private frame = 0;

  constructor() {
    this.mirror.texture.generateMipmaps = true;
    this.mirror.texture.minFilter = THREE.LinearMipmapLinearFilter;
  }

  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera,
    planet: Planet | null, extraWater: THREE.Object3D, clear: THREE.Color): boolean {
    const riverMaterial=planet?.riverWaterMaterial??null,wetlandMaterial=planet?.inlandWetlandWaterMaterial??null;
    const riverGroup=planet?.riverWaterGroup,wetlandGroup=planet?.inlandWetlandWaterGroup;
    const riverVisible=!!riverMaterial&&!!riverGroup?.visible,wetlandVisible=!!wetlandMaterial&&!!wetlandGroup?.visible;
    if (!planet || (!riverVisible&&!wetlandVisible)) return this.clear();
    planet.group.updateWorldMatrix(true, false);
    this.inverse.copy(planet.group.matrixWorld).invert();
    this.eye.copy(camera.position).applyMatrix4(this.inverse);
    const sampler = planet.sampler, graph = sampler.rivers(), R = planet.def.radius, A = sampler.amplitude;
    const farSquared = RIVER_REFLECTION_FAR_M * RIVER_REFLECTION_FAR_M;
    let best = farSquared;
    let candidateCount=0;
    // Project onto segments rather than snapping the mirror at each mesh row.
    if(riverVisible)for (const river of graph.rivers) for (let i = 1; i < river.points.length; i++) {
      const a = river.points[i-1], b = river.points[i];
      const ar = R+a.ws*A, br = R+b.ws*A;
      const ax=a.x*ar, ay=a.y*ar, az=a.z*ar;
      const dx=b.x*br-ax, dy=b.y*br-ay, dz=b.z*br-az;
      const t=THREE.MathUtils.clamp(((this.eye.x-ax)*dx+(this.eye.y-ay)*dy+(this.eye.z-az)*dz)/(dx*dx+dy*dy+dz*dz),0,1);
      this.candidate.set(ax+t*dx,ay+t*dy,az+t*dz);
      const distance=this.candidate.distanceToSquared(this.eye);
      if (distance < (candidateCount===this.riverCandidates.length?this.riverCandidates[candidateCount-1].distance:farSquared)) {
        this.screen.copy(this.candidate).applyMatrix4(planet.group.matrixWorld).project(camera);
        if(this.screen.z<=1 && Math.abs(this.screen.x)<1.1 && Math.abs(this.screen.y)<1.1) {
          let slot=Math.min(candidateCount,this.riverCandidates.length-1);
          while(slot>0&&distance<this.riverCandidates[slot-1].distance){
            this.riverCandidates[slot].point.copy(this.riverCandidates[slot-1].point);
            this.riverCandidates[slot].distance=this.riverCandidates[slot-1].distance;slot--;
          }
          this.riverCandidates[slot].point.copy(this.candidate);this.riverCandidates[slot].distance=distance;
          candidateCount=Math.min(candidateCount+1,this.riverCandidates.length);
        }
      }
    }
    // A route is only a coarse candidate. Settlement carving can remove its
    // water, and the route's old ramp does not describe a waterfall receiver.
    // Bound final terrain queries independently of total route count.
    for(let i=0;i<candidateCount;i++){
      this.candidate.copy(this.riverCandidates[i].point).normalize();
      const ws=this.riverWater(sampler,this.candidate);
      if(!Number.isFinite(ws))continue;
      this.candidate.multiplyScalar(R+ws*A+RIVER_SURFACE_LIFT_M);
      const distance=this.candidate.distanceToSquared(this.eye);
      this.screen.copy(this.candidate).applyMatrix4(planet.group.matrixWorld).project(camera);
      if(distance<farSquared&&this.screen.z<=1&&Math.abs(this.screen.x)<1.1&&Math.abs(this.screen.y)<1.1){
        best=distance;this.anchor.copy(this.candidate);break;
      }
    }
    let wetland=false;
    if(wetlandVisible&&nearestInlandWetlandReflection(wetlandGroup!,this.eye,this.wetlandCandidate,Math.sqrt(best),point=>{
      this.screen.copy(point).applyMatrix4(planet.group.matrixWorld).project(camera);
      return this.screen.z<=1&&Math.abs(this.screen.x)<1.1&&Math.abs(this.screen.y)<1.1;
    })){
      best=this.wetlandCandidate.distanceSquared;this.anchor.copy(this.wetlandCandidate.point);wetland=true;
    }
    if (best === farSquared) return this.clear();
    const material=wetland?wetlandMaterial!:riverMaterial!;
    if(this.material!==material){if(this.material)this.material.uniforms.uRiverReflStrength.value=0;this.material=material;this.fresh=false;}
    // Reach zero continuously at the same boundary that culls the probe, so
    // acquiring/losing it cannot switch directly between sky and a full mirror.
    const strength = .9 * (1-THREE.MathUtils.smoothstep(Math.sqrt(best),RIVER_REFLECTION_NEAR_M,RIVER_REFLECTION_FAR_M));
    if(wetland){
      // Cached points already lie on the exact shared local water sphere.
      // A closed basin has no invented current, sea gate or surface lift.
      this.normal.copy(this.anchor).normalize();
    }else{
      if(!this.riverNormal(sampler))return this.clear();
    }
    const sameReach=this.anchor.distanceToSquared(this.lastAnchor)<4&&this.normal.dot(this.lastNormal)>.9998;
    this.lastAnchor.copy(this.anchor);
    this.lastNormal.copy(this.normal);
    this.anchor.applyMatrix4(planet.group.matrixWorld);
    this.normal.transformDirection(planet.group.matrixWorld);
    this.screen.copy(this.anchor).project(camera);
    if (this.screen.z>1 || Math.abs(this.screen.x)>1.25 || Math.abs(this.screen.y)>1.25 ||
      this.candidate.subVectors(camera.position,this.anchor).dot(this.normal)<.05) return this.clear();
    const u=material.uniforms;
    u.uRiverReflTex.value=this.mirror.texture;
    u.uRiverReflAnchor.value.copy(this.anchor);
    u.uRiverReflPlane.value.set(this.normal.x,this.normal.y,this.normal.z,-this.normal.dot(this.anchor));
    this.frame++;
    if (this.fresh && sameReach && this.eye.distanceToSquared(this.mirrorEye)<.0144 && this.frame%2!==0) {
      // Carry a reused mirror through planet motion and floating-origin rebases.
      this.carry.copy(this.mirrorPose).multiply(this.inverse);
      u.uRiverReflMatrix.value.copy(this.mirror.textureMatrix).multiply(this.carry);
      this.updateView(camera);
      u.uRiverReflStrength.value=strength;
      return true;
    }
    const hidden: THREE.Object3D[]=[extraWater];
    if(riverGroup)hidden.push(riverGroup);if(wetlandGroup)hidden.push(wetlandGroup);
    // Opaque ice/lava belongs in freshwater reflections; only hide liquid water.
    if (planet.sea && !planet.basinSurface) hidden.push(planet.sea);
    const visibility=hidden.map(o=>o.visible);
    let ok=false;
    try {
      for (const o of hidden) o.visible=false;
      // Bound the single probe to half the drawing buffer. The former 256 px
      // height magnified tree silhouettes into visible stair steps.
      renderer.getDrawingBufferSize(this.viewport);
      const scale=Math.min(.5,1024/this.viewport.x,768/this.viewport.y);
      const width=Math.max(2,Math.round(this.viewport.x*scale));
      const height=Math.max(2,Math.round(this.viewport.y*scale));
      if(this.mirror.renderTarget.width!==width||this.mirror.renderTarget.height!==height)
        this.mirror.setSize(width,height);
      ok=this.mirror.render(renderer,scene,camera,this.anchor,this.normal,clear,1.35);
    } finally { hidden.forEach((o,i)=>{o.visible=visibility[i];}); }
    this.fresh=ok;
    u.uRiverReflStrength.value=ok?strength:0;
    if (ok) {
      u.uRiverReflMatrix.value.copy(this.mirror.textureMatrix);
      this.mirrorPose.copy(planet.group.matrixWorld);
      this.mirrorEye.copy(this.eye);
      this.updateView(camera);
    }
    return ok;
  }

  /** Final shared water ownership, including dry banks and below-water roads.
   * The short open-air lip transition has no standing reflective surface. */
  private riverWater(sampler:PlanetSampler,direction:THREE.Vector3,owner?:number,junction?:number):number {
    const hit=sampler.rivers().carveAt(direction.x,direction.y,direction.z);
    if(!hit||(owner!==undefined&&hit.riverId!==owner&&(junction===undefined||hit.junctionId!==junction))||
      (hit.fall&&hit.s>hit.fall.lip.s+1e-5&&hit.s<hit.fall.lip.s+FALL_LIP_RUN_M))return NaN;
    sampler.sample(direction.x,direction.y,direction.z,this.sample,false);
    const ws=this.sample.riverWs??-1e9,A=sampler.amplitude;
    return ws>-1e8&&(ws-sampler.seaRel)*A>=1&&(ws-this.sample.h)*A>.01&&
      this.sample.town<=.25&&this.sample.road>=0?ws:NaN;
  }

  /** A smoothed current vector is not the gradient of water at a bend. Fit a
   * bounded local tangent to the very same scalar water field as the ribbon.
   * Verified joining branches share a continuous surface. Near a dry edge,
   * unrelated ownership change or water-level step, use the sky fallback
   * instead of tilting a mirror across two incompatible receiving surfaces. */
  private riverNormal(sampler:PlanetSampler):boolean {
    const R=sampler.def.radius,A=sampler.amplitude,r=this.anchor.length();
    this.normal.copy(this.anchor).multiplyScalar(1/r);
    const hit=sampler.rivers().carveAt(this.normal.x,this.normal.y,this.normal.z);
    if(!hit)return false;
    this.flow.set(Math.abs(this.normal.x)<.9?1:0,Math.abs(this.normal.x)<.9?0:1,0)
      .addScaledVector(this.normal,-(Math.abs(this.normal.x)<.9?this.normal.x:this.normal.y)).normalize();
    this.across.crossVectors(this.normal,this.flow).normalize();
    const step=.5,ws=(r-R-RIVER_SURFACE_LIFT_M)/A;
    const height=(basis:THREE.Vector3,sign:number):number=>{
      this.probe.copy(this.normal).addScaledVector(basis,sign*step/r).normalize();
      return (this.riverWater(sampler,this.probe,hit.riverId,hit.junctionId)-ws)*A;
    };
    const u0=height(this.flow,-1),u1=height(this.flow,1),v0=height(this.across,-1),v1=height(this.across,1);
    if(![u0,u1,v0,v1].every(Number.isFinite)||Math.max(Math.abs(u0),Math.abs(u1),Math.abs(v0),Math.abs(v1))>.5||
      Math.abs(u0+u1)>.2||Math.abs(v0+v1)>.2)return false;
    this.normal.addScaledVector(this.flow,-(u1-u0)/(2*step))
      .addScaledVector(this.across,-(v1-v0)/(2*step)).normalize();
    return true;
  }

  private updateView(camera: THREE.PerspectiveCamera): void {
    const u=this.material!.uniforms;
    // Compose in JS doubles, before uploading: float fragment projection of
    // orbital-scale world positions visibly tears the reflected silhouette.
    u.uRiverReflViewMatrix.value.multiplyMatrices(u.uRiverReflMatrix.value,camera.matrixWorld);
    u.uRiverReflViewAnchor.value.copy(this.anchor).applyMatrix4(camera.matrixWorldInverse);
    this.viewPlane.setFromNormalAndCoplanarPoint(this.normal,this.anchor).applyMatrix4(camera.matrixWorldInverse);
    u.uRiverReflViewPlane.value.set(this.viewPlane.normal.x,this.viewPlane.normal.y,this.viewPlane.normal.z,this.viewPlane.constant);
  }

  private clear(): false {
    if (this.material) this.material.uniforms.uRiverReflStrength.value=0;
    this.fresh=false;
    return false;
  }

  dispose(): void { this.clear(); this.mirror.dispose(); }
}
